import 'modern-normalize'
import './assets/styles/style.css'
import { createApp, markRaw, watch } from 'vue'
import { ZiggyVue } from 'ziggy-js'
import theme from './config/primeVuePreset'
import PrimeVue from 'primevue/config'
import Toast from 'primevue/toast'
import ToastService from 'primevue/toastservice'
import CountryFlag from 'vue-country-flag-next'
import Tooltip from 'primevue/tooltip'
import { createPinia } from 'pinia'

import mixins from './helpers/mixins.js'
import ApiService from './api/ApiService'
import { useAuthStore } from './stores/auth'
import { useUserStore } from './stores/user'
import { useConsentStore } from './stores/consent'
import { loadZiggyRoutes } from './services/ziggy.js'
import { vueI18n } from './config/vueI18n.js'
import { initializeSentry } from './config/sentryConfig'
import {
    initGA4,
    setGA4User,
    markTimestamp,
    setDefaultConsent,
    updateConsent,
} from './config/analytics'
import { initHotjar, identifyHotjarUser } from './config/hotjar'
import { setupHotjarPrivacyGuard } from './config/hotjarPrivacy'
import { hjSuppressDirective } from './directives/hjSuppress'
import { getToastDuration } from './config/toastConfig.js'
import router, { isPartnerAllowedPath, partnerLandingFor } from './router'
import App from './App.vue'
import Login from './views/Auth/Login.vue'
import InternalLogin from './views/Auth/InternalLogin.vue'
import InternalRegister from './views/Auth/InternalRegister.vue'

const initVueApp = async () => {
    setDefaultConsent()

    const ziggyRoutes = await loadZiggyRoutes()

    if (ziggyRoutes) {
        const i18n = vueI18n()

        const pinia = createPinia()

        const app = createApp(App)

        initializeSentry(app, router, pinia)
        setupHotjarPrivacyGuard(router)

        app.use(ZiggyVue, ziggyRoutes)
        app.use(i18n)
        app.use(ToastService)
        const toast = app.config.globalProperties.$toast
        app.config.globalProperties.$getToastDuration = getToastDuration
        app.use(router)
        pinia.use(({ store }) => {
            store.router = markRaw(router)
            store.toast = markRaw(toast)
            store.i18n = markRaw(i18n)
            store.getToastDuration = getToastDuration
        })
        app.use(pinia)
        app.use(PrimeVue, theme)
        app.directive('tooltip', Tooltip)
        app.directive('hj-suppress', hjSuppressDirective)
        app.component('Toast', Toast)
        app.component('country-flag', CountryFlag)
        app.mixin(mixins)
        ApiService.init(app)

        app.component('login', Login)
        app.component('internal-login', InternalLogin)
        app.component('internal-register', InternalRegister)

        const authStore = useAuthStore()
        const consentStore = useConsentStore()
        consentStore.hydrate()

        if (authStore.isAuthenticated) {
            await authStore.fetchCurrentUser()
            if (!sessionStorage.getItem('login_ts')) {
                markTimestamp('login_ts')
            }

            // Lets leave this comment here for now, until we implement right solution (or probably forever)
            // The router's initial navigation starts at app.use(router), before user data is
            // fetched, so the beforeEach guard evaluates isPartner as false on a full page load.
            // Re-check here with hydrated data. window.location is used because currentRoute may
            // still be the start location while the initial navigation resolves.
            const userStore = useUserStore()
            if (userStore.isPartner && !isPartnerAllowedPath(window.location.pathname)) {
                await router.replace(partnerLandingFor(window.location.pathname))
            }
        }

        watch(
            () => [consentStore.analyticsGranted, consentStore.marketingGranted],
            ([analytics, marketing]) => {
                if (analytics) {
                    initGA4(router)
                    initHotjar(app)

                    if (authStore.isAuthenticated) {
                        const userStore = useUserStore()
                        setGA4User(userStore.userData)
                        identifyHotjarUser(userStore.userData, authStore.currentUserType)
                    }
                }
                updateConsent({ analytics, marketing })
            },
            { immediate: true }
        )

        app.mount('#vue-app')
    } else {
        console.error('Failed to load Ziggy routes. Vue app initialization aborted.')
    }
}

if (document.readyState === 'complete' || document.readyState !== 'loading') {
    initVueApp()
} else {
    document.addEventListener('DOMContentLoaded', initVueApp)
}
